import React, { useState, useEffect, useCallback, useRef } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import MediaPlayer from '../components/media/MediaPlayer';
import DisplayItemsList from '../components/dictionary/DisplayItemsList';
import Tooltip from '../components/Tooltip';
import Icon from '../components/Icon';
import { usePreferences } from '../context/UserPreferencesContext';
import { filterLearnerItems } from '../utils/learnerDisplay';
import SenseCard from '../learner/SenseCard';
import { loadYasna } from '../yasna/loadYasna';
import { fetchSnapshot, fetchLive } from '../learner/videoData';
import { isShowing, currentSubtitleIndexAt, subtitleFor, byOrder, byTitleOrder } from '../utils/timeline';
import { isTitleAnnotation } from '../annotations/annotationStore';
import { meaningOf, headwordOf, literalWith } from '../learner/meaning';
import { wordsOf, wordForWordLine, hasGlosses } from '../utils/wordForWord';
import { hasReference, referenceWordsIn } from '../utils/reference';
import { DEFAULT_MEDIA_URL } from '../config/defaultMedia';
import './PageShell.css';
import './HojaPage.css';

// An annotation points at a yasna sense (senseId). Annotations made before
// hoja used yasna point at hoja's own legacy dictionary (entryId) instead.
const annotationKey = (a) => a.senseId || a.entryId;

/**
 * HojaPage — Main learner page.
 * Video URL input → subtitle carousel synced to playback → tap words →
 * the yasna entry for the sense the annotator linked.
 */
/**
 * An annotated word with its meaning, as a pill: "le mouton » sheep"
 * (Requirements 52, 55). The dictionary's headword (article, and gender
 * where the article hides it) once known, else the annotated text; the
 * word alone until its meaning is known. Tapping opens the full entry.
 */
function WordChip({ ann, meaning, headword, active, onOpen }) {
  const word = headword || ann.surfaceText;
  // What it stands for here: "en » of it → les pommes" (Requirement 62)
  const ref = hasReference(ann) ? ann.refersTo.text : null;
  const label = (meaning ? `${word}: ${meaning}` : word) + (ref ? `, here: ${ref}` : '');
  return (
    <button
      type="button"
      className={`hoja-chip ${active ? 'hoja-chip--active' : ''}`}
      onClick={() => onOpen(ann)}
      aria-label={label}
    >
      <span className="hoja-chip-word">{word}</span>
      {meaning && (
        <>
          <span className="hoja-chip-sep" aria-hidden="true">»</span>
          <span className="hoja-chip-meaning">{meaning}</span>
        </>
      )}
      {ref && <span className="hoja-chip-ref">→ {ref}</span>}
    </button>
  );
}

function HojaPage() {
  const {
    pauseOnEverySubtitle,
    setPauseOnEverySubtitle,
    pauseIfUnknownWords,
    knownWordIds,
    skipKnownConfirm,
    addKnownWord,
    setSkipKnownConfirm,
  } = usePreferences();

  // Video state
  const [videoId, setVideoId] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);

  // Subtitles
  const [subtitles, setSubtitles] = useState([]);
  const [currentSubtitleIndex, setCurrentSubtitleIndex] = useState(-1);
  const [loadingSubtitles, setLoadingSubtitles] = useState(false);

  // Annotations for current subtitle
  const [annotations, setAnnotations] = useState([]);

  // Every annotation in this video, loaded once per video. The current
  // subtitle's annotations and the subtitle-free ones are taken from it, so
  // moving between subtitles costs no database read.
  const [videoAnnotations, setVideoAnnotations] = useState([]);

  // Annotations anchored to a time window instead of a subtitle
  const [momentAnnotations, setMomentAnnotations] = useState([]);
  // Each sense's meaning, for the word chips ("mouton » sheep"): from the
  // published snapshot at once, else from yasna as it loads.
  const [meanings, setMeanings] = useState({});
  // …and its headword ("le mouton", "l'eau (f.)"), shown in place of the
  // annotated text (Requirement 55).
  const [headwords, setHeadwords] = useState({});
  const addHeadwords = (more) => setHeadwords((prev) => {
    const fresh = Object.entries(more).filter(([k, v]) => v && prev[k] !== v);
    return fresh.length ? { ...prev, ...Object.fromEntries(fresh) } : prev;
  });
  // What each annotated word says literally in its role, by annotation id
  // ([{ word, gloss }]), for the word-for-word line (Requirement 61): from
  // the snapshot at once, then from yasna.
  const [literals, setLiterals] = useState({});
  // The role each annotation's literal was worked out for, so a sense is
  // only looked up again when an annotation's role has changed.
  const literalRolesRef = useRef(new Map());
  const addLiterals = (more) => setLiterals((prev) => {
    const fresh = Object.entries(more).filter(([k, v]) => v && JSON.stringify(prev[k]) !== JSON.stringify(v));
    return fresh.length ? { ...prev, ...Object.fromEntries(fresh) } : prev;
  });
  // The senses whose meaning is known or being looked up, so the snapshot
  // and the live data never ask yasna for the same one twice.
  const meaningIdsRef = useRef(new Set());
  const addMeanings = (more) => setMeanings((prev) => {
    const fresh = Object.entries(more).filter(([k, v]) => v && prev[k] !== v);
    return fresh.length ? { ...prev, ...Object.fromEntries(fresh) } : prev;
  });

  // Words of the video's title: shown for the whole video.
  const [titleAnnotations, setTitleAnnotations] = useState([]);
  // The title's subtitle (its English meaning), shown with them.
  const [titleSubtitle, setTitleSubtitle] = useState(null);
  // The title's meaning and words: collapsed until the learner opens them.
  const [titleOpen, setTitleOpen] = useState(false);

  // Tapped word annotation
  const [activeAnnotation, setActiveAnnotation] = useState(null);
  const [activeEntry, setActiveEntry] = useState(null);
  const [loadingEntry, setLoadingEntry] = useState(false);

  // Manual navigation ref — suppresses time-tracking effect after arrow nav
  const manualNavRef = useRef(false);

  // Entry navigation stack for linked references (legacy entries only —
  // yasna entries keep their own drill-down stack)
  const [entryStack, setEntryStack] = useState([]);

  // The yasna sense currently shown in the panel ({ senseId, entry }); differs
  // from the annotation's once the learner follows a reference.
  const [yasnaView, setYasnaView] = useState(null);

  // Known-words confirmation modal state
  const [showKnownConfirm, setShowKnownConfirm] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // Refs tracking which subtitle index each auto-pause branch has already acted on
  const prevEveryPauseIndexRef = useRef(-1);
  const prevUnknownPauseIndexRef = useRef(-1);
  // Subtitle-free annotations playback was inside on the last time update
  const activeMomentIdsRef = useRef(new Set());

  // --- Video handlers ---
  const handleVideoLoad = (id) => {
    setVideoId(id);
    setSubtitles([]);
    setVideoAnnotations([]);
    setMomentAnnotations([]);
    setMeanings({});
    setHeadwords({});
    setLiterals({});
    literalRolesRef.current = new Map();
    meaningIdsRef.current = new Set();
    setTitleAnnotations([]);
    setTitleSubtitle(null);
    setCurrentSubtitleIndex(-1);
    setAnnotations([]);
    setActiveAnnotation(null);
    setActiveEntry(null);
    setYasnaView(null);
  };

  const handleTimeUpdate = (time) => {
    setCurrentTime(time);
  };

  const handleVideoClear = () => {
    setVideoId(null);
    setCurrentTime(0);
    setSubtitles([]);
    setVideoAnnotations([]);
    setMomentAnnotations([]);
    setMeanings({});
    setHeadwords({});
    setLiterals({});
    literalRolesRef.current = new Map();
    meaningIdsRef.current = new Set();
    setTitleAnnotations([]);
    setTitleSubtitle(null);
    setCurrentSubtitleIndex(-1);
    setAnnotations([]);
    setActiveAnnotation(null);
    setActiveEntry(null);
    setYasnaView(null);
  };

  // --- Load subtitles for this video ---
  useEffect(() => {
    if (!videoId) return;
    loadVideoData(videoId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  // The video this page is showing now; a slow load for an earlier video
  // must not overwrite it.
  const shownVideoRef = useRef(null);

  // Subtitles and annotations: the published snapshot first (CDN, fast),
  // then the live data (Firestore REST) replaces it. No Firestore SDK and no
  // per-subtitle queries on the student page (learner/videoData.js).
  const loadVideoData = async (id) => {
    shownVideoRef.current = id;
    setLoadingSubtitles(true);
    // Start loading yasna now rather than on the first tap.
    const yasnaReady = Promise.resolve().then(() => loadYasna()).catch(() => null);

    const apply = ({ subtitles: subs, annotations: anns }) => {
      if (shownVideoRef.current !== id) return;
      // The title's subtitle has no time: it goes with the title, not the timeline.
      setSubtitles(subs.filter((x) => x.title !== true).sort((a, b) => a.startTime - b.startTime));
      setTitleSubtitle(subs.find((x) => x.title === true) || null);
      setVideoAnnotations(anns);
      setMomentAnnotations(anns.filter((a) => a.subtitleId == null));
      setTitleAnnotations(anns.filter(isTitleAnnotation));
      const withMeaning = anns.filter((a) => a.senseId && a.meaning);
      withMeaning.forEach((a) => meaningIdsRef.current.add(a.senseId));
      addMeanings(Object.fromEntries(withMeaning.map((a) => [a.senseId, a.meaning])));
      addHeadwords(Object.fromEntries(anns.filter((a) => a.senseId && a.headword).map((a) => [a.senseId, a.headword])));
      // The snapshot's literal: [{ word, gloss }], or null when the sense
      // has none (nothing to look up).
      const given = anns.filter((a) => a.senseId && a.literal !== undefined);
      given.forEach((a) => literalRolesRef.current.set(a.id, a.role || null));
      addLiterals(Object.fromEntries(given.filter((a) => Array.isArray(a.literal)).map((a) => [a.id, a.literal])));
      setLoadingSubtitles(false);
      // Load every linked yasna entry in the background, so a tapped word
      // opens with no database wait (yasna docs/hoja-integration.md §7).
      // The same reads give any meaning the snapshot didn't have.
      yasnaReady.then((yasna) => {
        if (!yasna) return;
        const ids = [...new Set(anns.map((a) => a.senseId).filter(Boolean))];
        if (typeof yasna.preload === 'function') yasna.preload(ids);
        if (typeof yasna.getById !== 'function') return;
        // An annotation whose literal is missing, or was for another role.
        const stale = (a) => !literalRolesRef.current.has(a.id) || literalRolesRef.current.get(a.id) !== (a.role || null);
        ids.forEach((sid) => {
          const needsLabels = !meaningIdsRef.current.has(sid);
          const needsLiterals = anns.some((a) => a.senseId === sid && stale(a));
          if (!needsLabels && !needsLiterals) return;
          meaningIdsRef.current.add(sid);
          // Already preloaded: no extra database read.
          Promise.resolve(yasna.getById(sid)).then(
            (entry) => {
              if (shownVideoRef.current !== id || !entry) return;
              if (needsLabels) {
                addMeanings({ [sid]: meaningOf(entry) });
                // yasna's own rule when it offers it, else the same rule here.
                addHeadwords({ [sid]: typeof yasna.headword === 'function' ? yasna.headword(entry) : headwordOf(entry) });
              }
              // Each annotation's literal gloss, in the role chosen for it.
              const mine = anns.filter((a) => a.senseId === sid);
              mine.forEach((a) => literalRolesRef.current.set(a.id, a.role || null));
              addLiterals(Object.fromEntries(mine.map((a) => [a.id, literalWith(yasna, entry, a.role)])));
            },
            () => { /* the chip shows the word alone */ }
          );
        });
      });
    };

    const live = fetchLive(id);
    let shown = false;
    try {
      apply(await fetchSnapshot(id));
      shown = true;
    } catch (e) {
      // Not published yet (a new video), or unreachable: the live data will do.
    }
    try {
      apply(await live);
    } catch (e) {
      console.error('Error loading subtitles and annotations:', e);
      if (!shown && shownVideoRef.current === id) setLoadingSubtitles(false);
    }
  };

  // --- Track current subtitle based on playback time ---
  useEffect(() => {
    if (subtitles.length === 0) {
      setCurrentSubtitleIndex(-1);
      return;
    }
    if (manualNavRef.current) return; // Don't override manual navigation
    // One subtitle at a time: without an end time, a subtitle lasts until
    // the next one starts (src/utils/timeline.js).
    const idx = currentSubtitleIndexAt(currentTime, subtitles);
    if (idx !== -1 && idx !== currentSubtitleIndex) {
      setCurrentSubtitleIndex(idx);
    }
  }, [currentTime, subtitles, currentSubtitleIndex]);

  // --- The current subtitle's annotations, from the video's list ---
  useEffect(() => {
    const sub = subtitles[currentSubtitleIndex];
    setAnnotations(sub ? videoAnnotations.filter((a) => a.subtitleId === sub.id) : []);
  }, [currentSubtitleIndex, subtitles, videoAnnotations]);

  // A new subtitle closes the open annotation panel, except when the
  // learner went back to hear what the open word refers to.
  const keepPanelRef = useRef(false);
  useEffect(() => {
    if (keepPanelRef.current) {
      keepPanelRef.current = false;
      return;
    }
    setActiveAnnotation(null);
    setActiveEntry(null);
  }, [currentSubtitleIndex]);

  // --- Navigation ---
  const goToSubtitle = useCallback((index) => {
    if (index < 0 || index >= subtitles.length) return;
    manualNavRef.current = true;
    setCurrentSubtitleIndex(index);
    const sub = subtitles[index];
    if (window.__hojaVideoSeekTo) {
      window.__hojaVideoSeekTo(sub.startTime);
    }
    if (window.__hojaVideoPlay) {
      window.__hojaVideoPlay();
    }
    // Allow time-tracking to resume after a brief delay (let the seek complete)
    setTimeout(() => { manualNavRef.current = false; }, 1000);
  }, [subtitles]);

  const goPrev = useCallback(
    () => goToSubtitle(currentSubtitleIndex - 1),
    [goToSubtitle, currentSubtitleIndex]
  );
  const goNext = useCallback(
    () => goToSubtitle(currentSubtitleIndex + 1),
    [goToSubtitle, currentSubtitleIndex]
  );

  // Replay the current subtitle: seek to its start and force play.
  const replayCurrent = useCallback(() => {
    if (currentSubtitleIndex < 0 || !subtitles[currentSubtitleIndex]) return;
    manualNavRef.current = true;
    const sub = subtitles[currentSubtitleIndex];
    if (window.__hojaVideoSeekTo) window.__hojaVideoSeekTo(sub.startTime);
    if (window.__hojaVideoPlay) window.__hojaVideoPlay();
    setTimeout(() => { manualNavRef.current = false; }, 1000);
  }, [currentSubtitleIndex, subtitles]);

  // --- Word tap handler ---
  const handleWordTap = (wordIndex) => {
    const knownSet = new Set(knownWordIds);
    // Only treat a word as tappable-annotated if its annotation's entry is unknown.
    const ann = annotations.find(
      (a) =>
        a.wordIndices &&
        a.wordIndices.includes(wordIndex) &&
        !knownSet.has(annotationKey(a))
    );
    if (ann) {
      openAnnotation(ann);
    } else {
      setActiveAnnotation(null);
      setActiveEntry(null);
      setEntryStack([]);
    }
  };

  const openAnnotation = (ann) => {
    // Pause the video when an annotation is opened. No auto-resume.
    if (window.__hojaVideoPause) window.__hojaVideoPause();
    setActiveAnnotation(ann);
    setEntryStack([]); // Reset navigation stack on new word tap
    setYasnaView(null);
    setActiveEntry(null);
    if (!ann.senseId) fetchEntry(ann.entryId);
  };

  const fetchEntry = async (entryId) => {
    setLoadingEntry(true);
    try {
      const entryRef = doc(db, 'dictionary', entryId);
      const snap = await getDoc(entryRef);
      if (snap.exists()) {
        setActiveEntry({ id: snap.id, ...snap.data() });
      } else {
        setActiveEntry(null);
      }
    } catch (e) {
      console.error('Error fetching entry:', e);
      setActiveEntry(null);
    }
    setLoadingEntry(false);
  };

  // --- Unified pause-priority decision for a subtitle transition ---
  // Priority: pauseOnEverySubtitle > pauseIfUnknownWords > continuous playback.
  const shouldPauseOnSubtitle = useCallback((subtitleIndex) => {
    if (pauseOnEverySubtitle) return true;
    if (pauseIfUnknownWords) {
      const sub = subtitles[subtitleIndex];
      if (!sub) return false;
      const kSet = new Set(knownWordIds);
      // annotations state holds the current subtitle's annotations once loaded.
      const subAnns = annotations.filter((a) => a.subtitleId === sub.id);
      return subAnns.some((a) => !kSet.has(annotationKey(a)));
    }
    return false;
  }, [pauseOnEverySubtitle, pauseIfUnknownWords, knownWordIds, subtitles, annotations]);

  // Auto-pause: pauseOnEverySubtitle acts immediately on index change (no
  // annotation dependency needed). Fires once per transition into a new subtitle.
  useEffect(() => {
    if (currentSubtitleIndex < 0) return;
    if (prevEveryPauseIndexRef.current === currentSubtitleIndex) return;
    prevEveryPauseIndexRef.current = currentSubtitleIndex;
    if (manualNavRef.current) return; // don't fight explicit manual nav/replay
    if (pauseOnEverySubtitle) {
      if (window.__hojaVideoPause) window.__hojaVideoPause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSubtitleIndex]);

  // Auto-pause: pauseIfUnknownWords needs this subtitle's annotations loaded, so
  // this effect also keys on `annotations` and re-evaluates after they load.
  // Guarded so it pauses at most once per subtitle transition.
  useEffect(() => {
    if (pauseOnEverySubtitle) return; // handled by the immediate effect above
    if (!pauseIfUnknownWords) return;
    if (currentSubtitleIndex < 0) return;
    if (prevUnknownPauseIndexRef.current === currentSubtitleIndex) return;
    if (manualNavRef.current) return;
    if (shouldPauseOnSubtitle(currentSubtitleIndex)) {
      prevUnknownPauseIndexRef.current = currentSubtitleIndex;
      if (window.__hojaVideoPause) window.__hojaVideoPause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSubtitleIndex, annotations, pauseOnEverySubtitle, pauseIfUnknownWords]);

  // Auto-pause without subtitles: on a video that has no subtitles, its
  // subtitle-free annotations stand in for them. Playback pauses when it
  // enters an annotation's time window, under the same settings: every
  // annotation with pauseOnEverySubtitle, only unknown ones with
  // pauseIfUnknownWords. Once per entry, so resuming inside a window plays on.
  useEffect(() => {
    if (loadingSubtitles || subtitles.length > 0) {
      activeMomentIdsRef.current = new Set();
      return;
    }
    const active = momentAnnotations.filter(
      (a) => isShowing(a, currentTime, subtitles, momentAnnotations)
    );
    const entered = active.filter((a) => !activeMomentIdsRef.current.has(a.id));
    activeMomentIdsRef.current = new Set(active.map((a) => a.id));
    if (entered.length === 0) return;
    const kSet = new Set(knownWordIds);
    const pause = pauseOnEverySubtitle
      || (pauseIfUnknownWords && entered.some((a) => !kSet.has(annotationKey(a))));
    if (pause && window.__hojaVideoPause) window.__hojaVideoPause();
  }, [currentTime, momentAnnotations, subtitles, loadingSubtitles,
    pauseOnEverySubtitle, pauseIfUnknownWords, knownWordIds]);

  // --- Keyboard shortcuts: a = prev, s = replay, d = next ---
  useEffect(() => {
    const handler = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
      if (e.key === 'a') { e.preventDefault(); goPrev(); }
      else if (e.key === 's') { e.preventDefault(); replayCurrent(); }
      else if (e.key === 'd') { e.preventDefault(); goNext(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goPrev, goNext, replayCurrent]);

  // --- Linked entry navigation ---
  const handleLinkedEntryClick = (linkedEntryId) => {
    if (!linkedEntryId || !activeEntry) return;
    // Push current entry onto stack
    setEntryStack((prev) => [...prev, activeEntry]);
    // Fetch the linked entry
    fetchEntry(linkedEntryId);
  };

  const handleAnnotationBack = () => {
    if (entryStack.length === 0) return;
    const prevEntry = entryStack[entryStack.length - 1];
    setEntryStack((prev) => prev.slice(0, -1));
    setActiveEntry(prevEntry);
  };

  // --- Mark-as-known ---
  // Marks the entry currently in the panel — the annotated sense, or the one
  // the learner drilled into.
  const knownTarget = activeAnnotation && activeAnnotation.senseId
    ? (yasnaView && yasnaView.entry
      ? { id: yasnaView.senseId, label: yasnaView.entry.word }
      : null)
    : (activeEntry ? { id: activeEntry.id, label: activeEntry.canonicalForm } : null);

  const closePanel = () => {
    setActiveAnnotation(null);
    setActiveEntry(null);
    setEntryStack([]);
    setYasnaView(null);
  };

  const markKnown = () => {
    addKnownWord(knownTarget.id);
    // The panel's word is no longer highlighted once its annotation is known.
    if (activeAnnotation && knownTarget.id === annotationKey(activeAnnotation)) closePanel();
  };

  const handleMarkKnown = () => {
    if (!knownTarget) return;
    if (skipKnownConfirm) {
      markKnown();
      return;
    }
    setShowKnownConfirm(true);
  };

  const handleConfirmKnown = () => {
    if (!knownTarget) return;
    if (dontShowAgain) setSkipKnownConfirm(true);
    setShowKnownConfirm(false);
    setDontShowAgain(false);
    markKnown();
  };

  // --- Build highlighted word indices from all annotations (unknown words only) ---
  const knownSet = new Set(knownWordIds);
  const annotatedIndices = new Set();
  annotations.forEach((ann) => {
    if (ann.wordIndices && !knownSet.has(annotationKey(ann))) {
      ann.wordIndices.forEach((i) => annotatedIndices.add(i));
    }
  });

  // Active span indices (for the tapped annotation)
  const activeIndices = new Set(
    activeAnnotation && activeAnnotation.wordIndices ? activeAnnotation.wordIndices : []
  );

  // What the open word refers to (Requirement 62): its subtitle, and the
  // words to mark when that subtitle is the one showing.
  const activeRef = activeAnnotation && hasReference(activeAnnotation) ? activeAnnotation.refersTo : null;
  const refSubIndex = activeRef ? subtitles.findIndex((x) => x.id === activeRef.subtitleId) : -1;
  const refIndices = new Set(activeRef && subtitles[currentSubtitleIndex]
    ? referenceWordsIn(activeRef, subtitles[currentSubtitleIndex]) : []);

  // Words of the title, unknown only: there for the whole video
  // The same word with the same meaning shows once, even when it was
  // annotated twice (e.g. an old annotation and a newer aligned one).
  const onePerWord = (anns) => {
    const seen = new Set();
    return anns.filter((a) => {
      const key = `${(a.surfaceText || '').trim().toLowerCase()}|${a.senseId || a.entryId || ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const titleWords = onePerWord(titleAnnotations.filter((a) => !knownSet.has(annotationKey(a))).sort(byTitleOrder));

  // Words annotated at this moment of the video (no subtitle), unknown only
  // Current subtitle
  const currentSub = subtitles[currentSubtitleIndex] || null;

  // The words shown with it. A word that belongs to a subtitle shows for as
  // long as that subtitle is on screen: the page keeps the last subtitle up
  // in a gap before the next one, so its words stay too (Requirement 49).
  // Words that belong to no subtitle follow their own times.
  const currentMoments = onePerWord(momentAnnotations
    .filter((a) => {
      if (knownSet.has(annotationKey(a))) return false;
      const own = subtitles.length > 0 ? subtitleFor(a.startTime, subtitles) : null;
      if (own) return !!currentSub && own.id === currentSub.id;
      return isShowing(a, currentTime, subtitles, momentAnnotations);
    })
    // In the teacher's order within the subtitle (Requirement 58)
    .sort(byOrder));
  // The current subtitle's word-for-word line (Requirement 61): its words,
  // each over what it says literally, from the annotated words' senses
  // (known ones too: the line is about the sentence). Shown whenever any
  // word has a gloss.
  const currentWords = currentSub ? wordsOf(currentSub.originalText) : [];
  const lineAnnotations = currentSub ? [
    ...annotations,
    ...momentAnnotations.filter((a) => {
      const own = subtitleFor(a.startTime, subtitles);
      return !!own && own.id === currentSub.id;
    }).sort(byOrder),
  ] : [];
  const literalLine = wordForWordLine(currentWords, lineAnnotations, literals);
  const showLiteral = hasGlosses(literalLine);
  // The gloss under each word as the subtitle is drawn (split the same way,
  // empty pieces included).
  let lineIndex = 0;
  const glossAt = (currentSub && currentSub.originalText ? currentSub.originalText.split(/\s+/) : [])
    .map((w) => (w ? (literalLine[lineIndex++] || {}).gloss || '' : ''));

  const prevSub = currentSubtitleIndex > 0 ? subtitles[currentSubtitleIndex - 1] : null;
  const nextSub = currentSubtitleIndex < subtitles.length - 1 ? subtitles[currentSubtitleIndex + 1] : null;

  // --- Helper: extract meaning for annotation header ---
  const getMeaning = (entry) => {
    if (entry.meaningEnglish) return entry.meaningEnglish;
    const firstItem = (entry.displayItems || [])[0];
    if (firstItem && firstItem.type === 'text' && firstItem.value) return firstItem.value;
    return '';
  };

  // Auto-pause on the next subtitle (or, without subtitles, the next
  // annotated word). Off by default; the same setting as in Settings.
  const autoPauseToggle = (
    <Tooltip label={pauseOnEverySubtitle ? 'Auto-pause is on: stop at each new line' : 'Auto-pause is off'}>
      <button
        type="button"
        className={`hoja-autopause ${pauseOnEverySubtitle ? 'hoja-autopause--on' : ''}`}
        aria-pressed={pauseOnEverySubtitle}
        onClick={() => setPauseOnEverySubtitle(!pauseOnEverySubtitle)}
      >
        <Icon name="pause" size={14} />
        <span>Auto-pause</span>
      </button>
    </Tooltip>
  );

  return (
    <div className="page-shell">
      <div className="page-shell-inner hoja-page">
        {/* Video input / player */}
        <MediaPlayer
          defaultVideoUrl={DEFAULT_MEDIA_URL}
          placeholder="Enter a YouTube URL to start"
          onVideoLoad={handleVideoLoad}
          onTimeUpdate={handleTimeUpdate}
          onVideoClear={handleVideoClear}
          titleContent={videoId && (titleWords.length > 0 || (titleSubtitle && titleSubtitle.englishText)) && (
            // The title's meaning and words: shown with the video (its
            // expand button), for the whole video
            <div className="hoja-title-extra">
              {/* Its own toggle, collapsed at first, so the title's meaning
                  and words take no room until wanted */}
              <button
                type="button"
                className="hoja-title-toggle"
                onClick={() => setTitleOpen(!titleOpen)}
                aria-expanded={titleOpen}
                aria-label={titleOpen ? 'Collapse the title\'s meaning and words' : 'Expand the title\'s meaning and words'}
              >
                <span className="hoja-title-label">Title</span>
                <span className="hoja-title-summary">
                  {[titleSubtitle && titleSubtitle.englishText && 'meaning',
                    titleWords.length > 0 && `${titleWords.length} word${titleWords.length === 1 ? '' : 's'}`]
                    .filter(Boolean).join(' · ')}
                </span>
                <Icon name={titleOpen ? 'chevron-up' : 'chevron-down'} size={16} />
              </button>
              {titleOpen && titleSubtitle && titleSubtitle.englishText && (
                <p className="hoja-title-english">{titleSubtitle.englishText}</p>
              )}
              {titleOpen && titleWords.length > 0 && (
                <div className="hoja-title-words" aria-label="Words from the title">
                  {titleWords.map((ann) => (
                    <WordChip key={ann.id} ann={ann} meaning={meanings[ann.senseId]} headword={headwords[ann.senseId]}
                      active={!!activeAnnotation && activeAnnotation.id === ann.id} onOpen={openAnnotation} />
                  ))}
                </div>
              )}
            </div>
          )}
        />

        {/* Subtitle area — only once video + subtitles are loaded */}
        {videoId && !loadingSubtitles && subtitles.length > 0 && (
          <div className="hoja-subtitle-section">
            {/* Carousel */}
            <div className="hoja-carousel">
              {/* Previous subtitle (blurred) */}
              <div className="hoja-carousel-item hoja-carousel-item--prev">
                {prevSub ? prevSub.originalText || prevSub.englishText : ''}
              </div>

              {/* Current subtitle (interactive) */}
              <div className="hoja-carousel-item hoja-carousel-item--current">
                {currentSub && currentSub.originalText ? (
                  <div
                    className={`hoja-subtitle-words${showLiteral ? ' hoja-subtitle-words--wfw' : ''}`}
                    aria-label={showLiteral ? `Word for word: ${literalLine.map((p) => p.gloss || '·').join(' ')}` : undefined}
                    role={showLiteral ? 'group' : undefined}
                  >
                    {currentSub.originalText.split(/\s+/).map((word, i) => {
                      const button = (
                        <button
                          key={i}
                          className={`hoja-word ${annotatedIndices.has(i) ? 'hoja-word--annotated' : ''} ${activeIndices.has(i) ? 'hoja-word--active' : ''} ${refIndices.has(i) ? 'hoja-word--ref' : ''}`}
                          onClick={() => handleWordTap(i)}
                          aria-label={`${word}${annotatedIndices.has(i) ? ' (annotated)' : ''}`}
                        >
                          {word}
                        </button>
                      );
                      if (!showLiteral) return button;
                      // Each word over what it says literally
                      return (
                        <span key={i} className="hoja-wfw-col">
                          {button}
                          <span className={`hoja-wfw-gloss${glossAt[i] ? '' : ' hoja-wfw-gloss--none'}`} aria-hidden="true">
                            {glossAt[i] || '·'}
                          </span>
                        </span>
                      );
                    })}
                  </div>
                ) : (
                  // An English-only subtitle (the French is in YouTube's own captions)
                  // shows just its English line below.
                  !currentSub && <span className="hoja-carousel-empty">—</span>
                )}
                {/* English translation below */}
                {currentSub && currentSub.englishText && (
                  <div className="hoja-subtitle-english">{currentSub.englishText}</div>
                )}
              </div>

              {/* Next subtitle (blurred) */}
              <div className="hoja-carousel-item hoja-carousel-item--next">
                {nextSub ? nextSub.originalText || nextSub.englishText : ''}
              </div>
            </div>

            {/* Navigation buttons */}
            <div className="hoja-nav">
              <Tooltip label="Previous subtitle (a)">
                <button
                  className="hoja-nav-btn"
                  onClick={goPrev}
                  disabled={currentSubtitleIndex <= 0}
                  aria-label="Previous subtitle (a)"
                >
                  <Icon name="chevron-left" size={20} />
                </button>
              </Tooltip>
              <Tooltip label="Replay current subtitle (s)">
                <button
                  className="hoja-nav-btn"
                  onClick={replayCurrent}
                  disabled={currentSubtitleIndex < 0}
                  aria-label="Replay current subtitle (s)"
                >
                  <Icon name="rotate-ccw" size={17} />
                </button>
              </Tooltip>
              <Tooltip label="Next subtitle (d)">
                <button
                  className="hoja-nav-btn"
                  onClick={goNext}
                  disabled={currentSubtitleIndex >= subtitles.length - 1}
                  aria-label="Next subtitle (d)"
                >
                  <Icon name="chevron-right" size={20} />
                </button>
              </Tooltip>
              {autoPauseToggle}
            </div>
          </div>
        )}

        {/* Without subtitles, auto-pause stops on annotated words */}
        {videoId && !loadingSubtitles && subtitles.length === 0 && momentAnnotations.length > 0 && (
          <div className="hoja-nav">{autoPauseToggle}</div>
        )}

        {/* Words annotated at this moment, without a subtitle */}
        {currentMoments.length > 0 && (
          <div className="hoja-moment-words" aria-label="Words in this scene">
            {currentMoments.map((ann) => (
              <WordChip key={ann.id} ann={ann} meaning={meanings[ann.senseId]} headword={headwords[ann.senseId]}
                active={!!activeAnnotation && activeAnnotation.id === ann.id} onOpen={openAnnotation} />
            ))}
          </div>
        )}

        {/* Loading state */}
        {videoId && loadingSubtitles && (
          <p className="hoja-loading">Loading subtitles…</p>
        )}

        {/* No subtitles message */}
        {videoId && !loadingSubtitles && subtitles.length === 0 && momentAnnotations.length === 0 && (
          <p className="hoja-no-subtitles">
            No subtitles available for this video yet. An annotator needs to add them first.
          </p>
        )}

        {/* Annotation panel */}
        {activeAnnotation && (
          <div className="hoja-annotation-panel">
            {activeAnnotation.annotatorNote && (
              <p className="hoja-annotation-note">{activeAnnotation.annotatorNote}</p>
            )}
            {activeRef && (
              // What this word stands for in the dialogue (Requirement 62)
              <p className="hoja-annotation-ref">
                Here, <strong>{activeAnnotation.surfaceText}</strong> refers to <strong>“{activeRef.text}”</strong>
                {refSubIndex !== -1 && refSubIndex !== currentSubtitleIndex && (
                  <>
                    {' '}
                    <button type="button" className="hoja-annotation-ref-go" onClick={() => { keepPanelRef.current = true; goToSubtitle(refSubIndex); }}
                      aria-label={`Hear “${activeRef.text}” again`}>
                      said earlier ↺
                    </button>
                  </>
                )}
              </p>
            )}
            {activeAnnotation.senseId ? (
              <SenseCard
                key={activeAnnotation.id}
                senseId={activeAnnotation.senseId}
                onNavigate={setYasnaView}
              />
            ) : (
              <>
                {loadingEntry && <p className="hoja-loading">Loading…</p>}
                {activeEntry && (
                  <>
                    <div className="hoja-annotation-header">
                      {entryStack.length > 0 && (
                        <button
                          className="hoja-annotation-back"
                          onClick={handleAnnotationBack}
                          aria-label="Back to previous entry"
                        >
                          ←
                        </button>
                      )}
                      <span className="hoja-annotation-canonical">{activeEntry.canonicalForm}</span>
                      {getMeaning(activeEntry) && (
                        <span className="hoja-annotation-meaning">{getMeaning(activeEntry)}</span>
                      )}
                    </div>
                    <DisplayItemsList
                      entryId={activeEntry.id}
                      items={filterLearnerItems(activeEntry.displayItems || [])}
                      showKeyNotes
                      language={activeEntry.language}
                      onUpdated={() => {}}
                      readOnly
                      onLinkedEntryClick={handleLinkedEntryClick}
                    />
                  </>
                )}
              </>
            )}
            {knownTarget && (
              <Tooltip label="Mark this word as known">
                <button
                  className="hoja-mark-known-btn"
                  onClick={handleMarkKnown}
                  aria-label="Mark as known"
                >
                  ✓ I know "{knownTarget.label}"
                </button>
              </Tooltip>
            )}
          </div>
        )}

        {/* Mark-as-known confirmation modal */}
        {showKnownConfirm && knownTarget && (
          <div
            className="hoja-known-modal-backdrop"
            onClick={() => setShowKnownConfirm(false)}
          >
            <div
              className="hoja-known-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <p>
                Mark "{knownTarget.label}" as known? It won't be
                highlighted again.
              </p>
              <label className="hoja-known-dontshow">
                <input
                  type="checkbox"
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                />
                Don't show this confirmation again
              </label>
              <div className="hoja-known-modal-actions">
                <button
                  className="hoja-known-confirm-btn"
                  onClick={handleConfirmKnown}
                >
                  Mark as known
                </button>
                <button
                  className="hoja-known-cancel-btn"
                  onClick={() => setShowKnownConfirm(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default HojaPage;
